/**
 * Email as Connect's staff run it for the whole installation: what an email
 * costs a client, the switch that stops every email, how mail is being
 * delivered, and the addresses found not to exist.
 *
 * Nothing here is decided by the screen. Whether a price may sit under its
 * minimum, whether a top-up is large enough, whether a stop can be lifted — the
 * API says, and what it says is shown where the value was typed.
 */

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  ReadOnly,
  ServerTable,
  Tabs,
  api,
  createServerFetcher,
  formatDateTime,
  toast,
  useCan,
  type ServerColumn,
} from '@connect/web-shared';

import { refusal, refusalText } from './api-refusal';
import {
  EMAIL_ENDPOINT,
  emailKeys,
  type BouncedAddress,
  type EmailLinkHosts,
  type EmailOverview,
  type EmailPricing,
  type PauseBody,
} from './email';
import { InboundPricingCard, InboundRegister } from './email-inbound';
import { EmailBudgetCards, EmailClientApiCard, EmailLimitsCard } from './email-limits';
import { PauseButton } from './email-pause';
import { EmailReviewQueue } from './email-review';
import { EmailReviewRules } from './email-review-rules';

const TABS = [
  { id: 'pricing', label: 'Pricing' },
  { id: 'sending', label: 'Sending' },
  { id: 'limits', label: 'Limits' },
  { id: 'budget', label: 'Budget & account' },
  { id: 'inbound', label: 'Incoming' },
  { id: 'reviews', label: 'Template review' },
  { id: 'rules', label: 'Review rules' },
  { id: 'bounced', label: 'Bounced addresses' },
] as const;

export function EmailPricingPage() {
  const [params, setParams] = useSearchParams();
  const overview = useQuery({ queryKey: emailKeys.overview, queryFn: () => api.get<EmailOverview>(EMAIL_ENDPOINT) });
  const asked = params.get('tab');
  const tab = TABS.some((item) => item.id === asked) ? (asked as string) : 'pricing';

  return (
    <div className="space-y-4">
      <PageHeader
        title="Email"
        description="What clients pay for email, how much each domain may send, what Connect spends at the mail service, and the review every client template passes before it is sent."
        tabs={
          <Tabs
            items={TABS}
            value={tab}
            onChange={(next) =>
              setParams(
                (previous) => {
                  const search = new URLSearchParams(previous);
                  search.set('tab', next);
                  return search;
                },
                { replace: true },
              )
            }
          />
        }
      />
      {tab === 'bounced' ? (
        <BouncedRegister />
      ) : tab === 'inbound' ? (
        <InboundRegister />
      ) : tab === 'reviews' ? (
        <EmailReviewQueue />
      ) : tab === 'rules' ? (
        <EmailReviewRules />
      ) : overview.isLoading ? (
        <LoadingState label="Loading email" />
      ) : overview.error || !overview.data ? (
        <ErrorState
          message={overview.error instanceof ApiError ? overview.error.message : 'Email could not be loaded.'}
          onRetry={() => void overview.refetch()}
        />
      ) : tab === 'sending' ? (
        <SendingCards overview={overview.data} />
      ) : tab === 'limits' ? (
        <div className="space-y-4">
          <EmailLimitsCard key={JSON.stringify(overview.data.limits)} limits={overview.data.limits} />
          <EmailClientApiCard key={JSON.stringify(overview.data.client_api)} policy={overview.data.client_api} />
        </div>
      ) : tab === 'budget' ? (
        <EmailBudgetCards key={JSON.stringify(overview.data.budget)} overview={overview.data} />
      ) : (
        // Keyed on what is saved: a colleague's save replaces a stale draft.
        <div className="space-y-4">
          <PricingEditor key={JSON.stringify(overview.data.pricing)} pricing={overview.data.pricing} />
          <InboundPricingCard key={`inbound-${JSON.stringify(overview.data.pricing)}`} pricing={overview.data.pricing} />
        </div>
      )}
    </div>
  );
}

// ── the price ───────────────────────────────────────────────
function PricingEditor({ pricing }: { pricing: EmailPricing }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.email.edit');
  const saved = {
    price: pricing.price,
    minimum_price: pricing.minimum_price,
    attachment_max_mb: String(pricing.attachment_max_mb),
    attachment_price_per_mb: pricing.attachment_price_per_mb,
  };
  const [draft, setDraft] = useState(saved);
  const changed = (Object.keys(saved) as (keyof typeof saved)[]).some((key) => draft[key] !== saved[key]);

  const save = useMutation({
    mutationFn: () =>
      api.put<EmailOverview>(`${EMAIL_ENDPOINT}/pricing`, {
        ...draft,
        attachment_max_mb: Number(draft.attachment_max_mb),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(emailKeys.overview, data);
      toast.success('Email prices saved', 'Emails sent from now on are charged at them.');
    },
  });
  // The server's answer, under what it is about. Typing again clears it: it
  // was about the values that were sent, not the ones now on screen.
  const refused = refusal(save.error);
  const set = (field: keyof typeof draft, value: string) => {
    save.reset();
    setDraft((was) => ({ ...was, [field]: value.trim() }));
  };

  return (
    <ReadOnly when={!canEdit}>
      <Card>
        <CardHeader
          title="Price of an email"
          description="A client with a price of its own is still never charged under the minimum, so raising the minimum protects every client's price at once."
        />
        <CardBody className="space-y-4">
          {canEdit ? null : <Alert tone="info">You can see these, not change them.</Alert>}
          <div className="grid gap-3 sm:grid-cols-3">
            <Input
              label="Price per email (₹)"
              inputMode="decimal"
              value={draft.price}
              hint="What a client pays unless it has its own price."
              error={refused?.fields.price}
              onChange={(event) => set('price', event.target.value)}
            />
            <Input
              label="Minimum price (₹)"
              inputMode="decimal"
              value={draft.minimum_price}
              hint="No client's own price goes under this."
              error={refused?.fields.minimum_price}
              onChange={(event) => set('minimum_price', event.target.value)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Input
              label="Files per email, at most (MB)"
              inputMode="numeric"
              value={draft.attachment_max_mb}
              hint="What an email written in a client's inbox may carry. 1 to 25."
              error={refused?.fields.attachment_max_mb}
              onChange={(event) => set('attachment_max_mb', event.target.value)}
            />
            <Input
              label="Per MB of files (₹)"
              inputMode="decimal"
              value={draft.attachment_price_per_mb}
              hint="Added to an email's price for each started MB of its files, for each recipient. 0 adds nothing."
              error={refused?.fields.attachment_price_per_mb}
              onChange={(event) => set('attachment_price_per_mb', event.target.value)}
            />
          </div>
          <p className="text-xs text-muted">Prices are in {pricing.currency}, before GST.</p>
          {refused?.general.length ? (
            <Alert tone="danger">{refused.general.join(' ')}</Alert>
          ) : null}
          {canEdit && changed ? (
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  save.reset();
                  setDraft(saved);
                }}
              >
                Discard
              </Button>
              <Button loading={save.isPending} onClick={() => save.mutate()}>
                Save prices
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </ReadOnly>
  );
}

// ── the kill switch, and how mail is delivered ──────────────
/** The backend as the API names it, in words. An unknown one is shown as it came. */
const BACKENDS: Record<string, { label: string; meaning: string }> = {
  ses: { label: 'Amazon SES', meaning: 'Email is sent through Amazon SES.' },
  console: {
    label: 'Log only',
    meaning: 'Email is written to the server log and delivered to nobody. This is a development setting.',
  },
};

function SendingCards({ overview }: { overview: EmailOverview }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.email.edit');
  const { sending } = overview;
  const backend = BACKENDS[overview.backend] ?? {
    label: overview.backend,
    meaning: `Email is sent through the “${overview.backend}” backend.`,
  };

  const set = useMutation({
    mutationFn: (body: PauseBody) => api.put<EmailOverview>(`${EMAIL_ENDPOINT}/sending`, body),
    onSuccess: (data) => {
      queryClient.setQueryData(emailKeys.overview, data);
      toast.success(
        data.sending.paused ? 'All email paused' : 'Email resumed',
        data.sending.paused
          ? "No client's email, and none of Connect's own, is sent."
          : data.sending.paused_by_environment
            ? 'This stop is lifted. The environment still pauses email.'
            : 'Email is sent again.',
      );
    },
  });

  const stopped = sending.paused || sending.paused_by_environment;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="All email"
          description="The switch that stops every email at once — every client's, and Connect's own. A send that is refused costs the client nothing."
          actions={<Badge tone={stopped ? 'danger' : 'success'}>{stopped ? 'Paused' : 'Sending'}</Badge>}
        />
        <CardBody className="space-y-3">
          {sending.paused_by_environment ? (
            <Alert tone="warning">
              Email is paused by this installation's environment. It cannot be resumed from this screen: whoever runs the
              server turns that setting off and restarts the API.
            </Alert>
          ) : null}
          {sending.paused ? (
            <Alert tone="danger">
              All email was paused here.{sending.note ? ` Note: ${sending.note}` : ' No note was left.'}
            </Alert>
          ) : null}
          {stopped ? null : <p className="text-sm text-muted">Email is being sent.</p>}
          {/* While only the environment stops email there is nothing here to
              lift, and a pause added on top would be a second stop to forget. */}
          {canEdit && (sending.paused || !sending.paused_by_environment) ? (
            <PauseButton
              paused={sending.paused}
              subject="all email"
              size="md"
              consequence="Every client's email and Connect's own stop until somebody resumes them. Scheduled and queued email is refused too."
              resumed={
                sending.paused_by_environment
                  ? 'This stop is lifted. Email stays paused by the environment until that is turned off too.'
                  : "Every client's email and Connect's own are sent again. A client or a domain paused on its own stays paused."
              }
              onSet={(body) => set.mutateAsync(body)}
            />
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="How email is delivered" description="Set where the installation is configured, and shown here as it is." />
        <CardBody>
          <dl className="grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-2xs uppercase tracking-wide text-muted">Mail service</dt>
              <dd className="text-sm font-medium text-heading">{backend.label}</dd>
              <dd className="text-xs text-muted">{backend.meaning}</dd>
            </div>
            <div>
              <dt className="text-2xs uppercase tracking-wide text-muted">Delivery reports</dt>
              <dd className="text-sm font-medium text-heading">
                {/* Nothing is delivered when email is only logged, so there is
                    nothing to report on: "set up" there would claim a link to
                    a mail service that does not exist. */}
                {overview.backend === 'console'
                  ? 'Not needed'
                  : overview.reports_configured
                    ? 'Set up'
                    : 'Not set up'}
              </dd>
              <dd className="text-xs text-muted">
                {overview.backend === 'console'
                  ? 'No email leaves this installation, so there are no bounces to hear about.'
                  : overview.reports_configured
                    ? 'Bounces and spam complaints reach Connect, and those addresses stop being emailed.'
                    : 'Bounces and spam complaints do not reach Connect yet, so nothing is learned from them.'}
              </dd>
            </div>
          </dl>
        </CardBody>
      </Card>

      {overview.links ? <LinkHostsCard links={overview.links} /> : null}
    </div>
  );
}

/**
 * Clients' own link addresses: what their `links` record points at, how many
 * are in use, and the ones the machine's edge has no certificate for yet —
 * the operator's list. Their mail sends meanwhile, with Connect's own link.
 */
function LinkHostsCard({ links }: { links: EmailLinkHosts }) {
  return (
    <Card>
      <CardHeader
        title="Clients' link addresses"
        description={`Each client domain's unsubscribe links move to links.<its domain> once that address answers here over a valid certificate. ${links.ready} in use.`}
        actions={
          <Badge tone={links.waiting.length ? 'warning' : 'success'}>
            {links.waiting.length ? `${links.waiting.length} waiting` : 'None waiting'}
          </Badge>
        }
      />
      <CardBody className="space-y-3">
        <p className="text-xs text-muted">
          Clients point <span className="font-mono">links</span> at <span className="font-mono">{links.target}</span>.
        </p>
        {links.waiting.length ? (
          <>
            <ul className="divide-y divide-line">
              {links.waiting.map((item) => (
                <li key={item.host} className="flex flex-wrap items-baseline justify-between gap-2 py-2 first:pt-0">
                  <span className="break-all font-mono text-sm text-heading">{item.host}</span>
                  <span className="text-xs text-muted">
                    {item.client_name}
                    {item.checked_at ? ` · seen ${formatDateTime(item.checked_at)}` : ''}
                  </span>
                </li>
              ))}
            </ul>
            <Alert tone="info">
              Their records are in place; the server's edge needs a certificate for each. Whoever runs the server runs{' '}
              <span className="font-mono">scripts/place-link-hosts.sh</span>, and they are used within a quarter of an
              hour.
            </Alert>
          </>
        ) : (
          <p className="text-sm text-muted">No link address is waiting for a certificate.</p>
        )}
      </CardBody>
    </Card>
  );
}

// ── addresses that do not exist ─────────────────────────────
function BouncedRegister() {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.email.edit');
  const [removing, setRemoving] = useState<BouncedAddress | null>(null);
  const fetchRows = useMemo(() => createServerFetcher<BouncedAddress>(`${EMAIL_ENDPOINT}/bounced`), []);

  const remove = useMutation({
    mutationFn: (row: BouncedAddress) => api.delete<null>(`${EMAIL_ENDPOINT}/bounced/${row.public_id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: emailKeys.bounced });
      toast.success('Removed', 'Email goes to that address again.');
    },
    onError: (error) => toast.error('Not removed', refusalText(error)),
  });

  const columns = useMemo<ServerColumn<BouncedAddress>[]>(
    () => [
      { field: 'address', header: 'Address' },
      { field: 'created_at', header: 'Found', width: 190, render: (row) => formatDateTime(row.created_at) },
      // The column is there only for somebody who may remove.
      ...(canEdit
        ? [
            {
              field: 'public_id',
              header: '',
              width: 60,
              sortable: false,
              render: (row: BouncedAddress) => (
                <Button variant="ghost" size="sm" aria-label={`Remove ${row.address}`} onClick={() => setRemoving(row)}>
                  <Trash2 className="size-3.5" />
                </Button>
              ),
            } satisfies ServerColumn<BouncedAddress>,
          ]
        : []),
    ],
    [canEdit],
  );

  return (
    <>
      <ServerTable<BouncedAddress>
        columns={columns}
        fetchRows={fetchRows}
        rowKey={(row) => row.public_id}
        queryKey={emailKeys.bounced}
        stateKey="messaging.email.bounced"
        searchPlaceholder="Search by address"
        emptyTitle="No bounced addresses"
        emptyDescription="An address a mail server says does not exist is listed here, and no client's email is sent to it."
      />
      <Confirm
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={`Remove ${removing?.address ?? ''}?`}
        confirmLabel="Remove"
        message="Every client's email goes to that address again — for a mailbox created since, or a bounce that was wrong. A client's own do-not-email list is not changed."
        onConfirm={async () => {
          // The mutation reports its own failure; the dialog only closes.
          if (removing) await remove.mutateAsync(removing).catch(() => undefined);
        }}
      />
    </>
  );
}
